import React, { useEffect, useState, useRef } from "react";
import StepWizard from "react-step-wizard";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import MultiSelectComponent from "../../../components/Tenant/MultiSelectComponent/MultiSelectComponent";
import { States } from "../../../utils/getStates";
import { FaCloudUploadAlt } from "react-icons/fa";
import { IoArrowBackOutline, IoArrowForwardOutline } from "react-icons/io5";
import { Button } from "@mui/material";
import { useAddWheelCampaignMutation } from "../../../features/tenant/wheelCampaign/wheelCampaignApi";
import { useAddWheelMutation, useGetWheelByCampaignIdMutation } from "../../../features/tenant/wheel/wheelApi";
import { useAddWheelRewardsMutation } from "../../../features/tenant/wheelRewards/wheelRewardsApi";
import { useGetAppUserTypesMutation } from "../../../features/tenant/appUsers/appUsersApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import Wheels from "../../../components/Tenant/Wheel/Wheel";
import { Modal, Box } from "@mui/material";
import "../../Admin/Vendor/wizard.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const FEEDBACK_TYPES = [
  { id: 1, name: "Not Applicable" },
  { id: 2, name: "Before Spin" },
  { id: 3, name: "After Spin" },
];

const STATUS_OPTIONS = [
  { id: "1", name: "Inactive" },
  { id: "2", name: "Active" },
];

const WHEEL_TYPES = [
  { id: "1", name: "Probability" },
  { id: "2", name: "Fixed Prize" },
];

const REWARD_TYPES = [
  { id: "Filler", name: "Filler" },
  { id: "Gift", name: "Gift" },
];

const SEGMENT_OPTIONS = Array.from({ length: 9 }, (_, i) => ({
  id: i + 2,
  name: i + 2,
})); // 2–10

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getToken = () => sessionStorage.getItem("access_token");
const getTenantId = () =>
  JSON.parse(sessionStorage.getItem("vendorData") || "{}")?.tenant_id;

const makeBlankReward = (index, startDate = "", endDate = "") => ({
  windex: index,
  type: "",
  name: index === 0 ? "Better Luck Next Time" : "",
  status: "2",
  startDate,
  endDate,
  probability: "",
  quantity: "",
  image: "",
  points: "",
  price: "",
  coupon_type: "",
  font_size: "",
  is_reward: "0",
  locations: [],
});

// ─── Sub-component: numeric text field ───────────────────────────────────────

const NumericField = ({ label, value, onChange, placeholder = "0", required = false, className = "w-full border rounded p-2 text-sm" }) => (
  <div>
    {label && (
      <label className="block text-xs font-medium text-gray-700 mb-0.5">
        {label}{required && " *"}
      </label>
    )}
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*\.?[0-9]*"
      className={className}
      value={value}
      onChange={(e) => {
        // Allow digits, optional single decimal point, optional leading minus
        const v = e.target.value;
        if (v === "" || v === "-" || /^-?\d*\.?\d*$/.test(v)) onChange(v);
      }}
      placeholder={placeholder}
    />
  </div>
);

// ─── Sub-component: ActionButtons ─────────────────────────────────────────────

const ActionButtons = ({
  currentStep,
  totalSteps,
  previousStep,
  nextStep,
  onStep1Next,
  onStep2Next,
  onStep4Submit,
  isSubmitting,
}) => {
  const handleNext = () => {
    if (currentStep === 1) onStep1Next?.();
    else if (currentStep === 2) onStep2Next?.();
    else if (currentStep === 4) onStep4Submit?.();
    else nextStep?.();
  };

  return (
    <div className="mt-4 flex justify-center gap-3">
      {currentStep > 1 && (
        <Button
          startIcon={<IoArrowBackOutline />}
          variant="outlined"
          onClick={previousStep}
          disabled={isSubmitting}
        >
          Back
        </Button>
      )}
      {currentStep < totalSteps ? (
        <Button
          variant="outlined"
          endIcon={<IoArrowForwardOutline />}
          color="success"
          onClick={handleNext}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Please wait…" : "Next"}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="success"
          onClick={handleNext}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting…" : "Finish"}
        </Button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AddWheel = () => {
  const navigate = useNavigate();
  const allStates = States || [];
  const stepWizardRef = useRef(null);

  // UI state
  const [activeStep, setActiveStep] = useState(1); // StepWizard is 1-indexed
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // IDs produced by API calls
  const [wcId, setWcId] = useState(null);
  const [wheelId, setWheelId] = useState(null);

  // ── Step 1: Campaign Info ─────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [budget, setBudget] = useState("");
  const [scanPerTicket, setScanPerTicket] = useState("");
  const [states, setStates] = useState(allStates[0] ? [allStates[0]] : []);
  const [campaignStatus, setCampaignStatus] = useState(STATUS_OPTIONS[1]); // Active
  const [feedbackForm, setFeedbackForm] = useState(FEEDBACK_TYPES[0]);
  const [elements] = useState([]); // form builder not yet wired

  // ── Step 2: Wheel Design ──────────────────────────────────────────────────
  const [segments, setSegments] = useState(SEGMENT_OPTIONS[0]); // default: 2
  const [wheelType, setWheelType] = useState({ id: "0", name: "Set Wheel Type" });
  const [userTypeNames, setUserTypeNames] = useState([]);
  const [picture, setPicture] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);

  // ── Steps 3 & 4: Rewards array ────────────────────────────────────────────
  const [rewards, setRewards] = useState([]);

  // ── API hooks ─────────────────────────────────────────────────────────────
  const [addWheelCampaign, { data: addCampaignData, error: addCampaignError }] =
    useAddWheelCampaignMutation();
  const [addWheel, { data: addWheelData, error: addWheelError }] =
    useAddWheelMutation();
  const [getWheelByCampaignId, { data: wheelByCampaignData }] =
    useGetWheelByCampaignIdMutation();
  const [addWheelRewards] = useAddWheelRewardsMutation();
  const [getAppUserTypes, { data: appUserTypesData }] =
    useGetAppUserTypesMutation();
  const [uploadImage, { data: uploadImageData, error: uploadImageError }] =
    useUploadSingleFileMutation();

  const userTypes = appUserTypesData?.body || [];

  // ── Effects ───────────────────────────────────────────────────────────────

  // Load user types once
  useEffect(() => {
    getAppUserTypes({ tenant_id: getTenantId() });
  }, []);

  // Image upload result
  useEffect(() => {
    if (uploadImageData?.body?.fileLink) setPicture(uploadImageData.body.fileLink);
  }, [uploadImageData]);
  useEffect(() => {
    if (uploadImageError) toast.error("Image upload failed");
  }, [uploadImageError]);

  // Step 1 → campaign created
  useEffect(() => {
    if (!addCampaignData) return;
    const id =
      addCampaignData?.body?.id ??
      addCampaignData?.body?.wheel_campaign_id ??
      addCampaignData?.body?.data?.id ??
      addCampaignData?.id;
    if (id) {
      setWcId(id);
      setIsSubmitting(false);
      toast.success("Campaign created");
      stepWizardRef.current?.nextStep();
    } else {
      setIsSubmitting(false);
      toast.error("Could not read campaign ID from response");
    }
  }, [addCampaignData]);

  useEffect(() => {
    if (!addCampaignError) return;
    setIsSubmitting(false);
    toast.error("Error creating campaign");
  }, [addCampaignError]);

  // Step 2 → wheel created → fetch wheel by campaign to get wheel ID
  useEffect(() => {
    if (addWheelData && wcId) {
      getWheelByCampaignId({ token: getToken(), tenant_id: getTenantId(), id: wcId });
    }
  }, [addWheelData, wcId]);

  useEffect(() => {
    if (!addWheelError) return;
    setIsSubmitting(false);
    toast.error("Error creating wheel");
  }, [addWheelError]);

  useEffect(() => {
    if (wheelByCampaignData?.body?.length) {
      setWheelId(wheelByCampaignData.body[0].id);
      setIsSubmitting(false);
      toast.success("Wheel created");
      stepWizardRef.current?.nextStep();
    }
  }, [wheelByCampaignData]);

  // Sync rewards array when segment count changes (preserve existing edits)
  useEffect(() => {
    const n = Number(segments?.name ?? segments?.id ?? 2);
    if (n < 2 || n > 10) return;
    setRewards((prev) =>
      Array.from({ length: n }, (_, i) =>
        prev[i] ? { ...prev[i], windex: i } : makeBlankReward(i, startDate, endDate)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments?.name, segments?.id]);

  // ── Validation ────────────────────────────────────────────────────────────

  const validateStep1 = () => {
    if (!title.trim()) { toast.error("Title is required"); return false; }
    if (!description.trim()) { toast.error("Description is required"); return false; }
    if (!startDate) { toast.error("Start date is required"); return false; }
    if (!endDate) { toast.error("End date is required"); return false; }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error("End date must be ≥ start date"); return false;
    }
    const b = Number(budget);
    if (!budget || isNaN(b) || b <= 0) { toast.error("Budget must be > 0"); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!wheelType?.id || wheelType.id === "0") { toast.error("Select wheel type"); return false; }
    if (!picture) { toast.error("Please upload wheel image"); return false; }
    if (!userTypeNames?.length) { toast.error("Select at least one user type"); return false; }
    return true;
  };

  const validateStep4 = () => {
    let sum = 0;
    for (const r of rewards) {
      if (!r.type) { toast.error("Reward type required for all segments"); return false; }
      if (!r.name?.trim()) { toast.error("Reward name required for all segments"); return false; }
      if (!r.startDate || !r.endDate) {
        toast.error("Start/End date required for each reward"); return false;
      }
      sum += Number(r.probability) || 0;
    }
    if (sum > 100) { toast.error("Total probability cannot exceed 100%"); return false; }
    return true;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleStep1Next = () => {
    if (!validateStep1()) return;
    setIsSubmitting(true);
    const finalStates = states.map((s) => (typeof s === "object" ? s.name : s));
    addWheelCampaign({
      token: getToken(),
      tenant_id: getTenantId(),
      data: {
        title: title.trim(),
        description: description.trim(),
        start_date: startDate,
        end_date: endDate,
        budget: String(budget),
        scan_per_ticket: Number(scanPerTicket) || 0,
        states: finalStates,
        status: campaignStatus?.id ?? "2",
        feedback_form: feedbackForm?.id ?? 1,
        form_template: JSON.stringify(elements),
      },
    });
  };

  const handleStep2Next = () => {
    if (!validateStep2()) return;
    setIsSubmitting(true);
    const userTypeIds = userTypeNames.map((o) => o.user_type_id ?? o.id);
    const userTypeNamesList = userTypeNames.map((o) => o.user_type ?? o.name);
    addWheel({
      token: getToken(),
      tenant_id: getTenantId(),
      data: {
        wc_id: wcId,
        reward_count: segments?.name ?? segments?.id ?? 2,
        wheel_type: wheelType,
        user_type: userTypeNamesList,
        user_type_id: userTypeIds,
        wheel_image: picture,
      },
    });
  };

  const updateReward = (index, key, value) =>
    setRewards((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [key]: value } : r))
    );

  const handleStep4Submit = async () => {
    if (!validateStep4() || !wheelId || !wcId) return;
    setIsSubmitting(true);
    const token = getToken();
    const tenant_id = getTenantId();
    try {
      for (let i = 0; i < rewards.length; i++) {
        const r = rewards[i];
        const formData = new FormData();
        formData.append("wc_id", wcId);
        formData.append("w_id", wheelId);
        formData.append("type", r.type || "Filler");
        formData.append("name", r.name || "");
        formData.append("status", r.status || "2");
        formData.append("windex", String(i));
        formData.append("startDate", r.startDate || startDate);
        formData.append("endDate", r.endDate || endDate);
        formData.append("offsetx", "0");
        formData.append("offsety", "0");
        formData.append("probability", String(Number(r.probability) || 0));
        formData.append("quantity", String(Number(r.quantity) || 0));
        formData.append("image", r.image || "");
        formData.append("points", String(Number(r.points) || 0));
        formData.append("price", String(r.price ?? ""));
        formData.append("coupon_type", r.coupon_type || "");
        formData.append("font_size", r.font_size || "");
        formData.append("is_reward", r.is_reward || "0");
        const locationsStr = Array.isArray(r.locations)
          ? r.locations
              .map((s) => (typeof s === "object" ? s.name : s))
              .filter(Boolean)
              .join(",")
          : "";
        formData.append("locations", locationsStr);
        await addWheelRewards({ token, tenant_id, data: formData }).unwrap();
      }
      toast.success("Wheel campaign created successfully");
      navigate("/wheel-list");
    } catch {
      toast.error("Error adding rewards");
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadImageFunc = () => {
    if (!pictureFile?.length) return;
    const formData = new FormData();
    formData.append("image", pictureFile[0]);
    uploadImage({ body: formData });
  };

  const assignStepWizard = (instance) => {
    if (instance) stepWizardRef.current = instance;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const STEP_LABELS = ["Campaign Info", "Wheel Design", "Rewards", "Allocation"];

  return (
    <div className="flex w-full bg-slate-200 min-h-full justify-center">
      <div className="w-full">
        {/* Page header */}
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
          Add Wheel Campaign
          <LinksComponent />
        </div>

        <div className="p-3">
          <div
            className="p-3 w-full bg-white shadow rounded-lg formsstep_n1 width50 mt-4"
            style={{ maxWidth: "90%", margin: "0 auto" }}
          >
            {/* Step nav + preview */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-1 sm:gap-2 flex-1 min-w-0">
                {STEP_LABELS.map((label, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => stepWizardRef.current?.goToStep?.(idx + 1)}
                    className={`px-3 py-1.5 rounded text-sm font-medium border transition ${
                      activeStep === idx + 1
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {idx + 1}. {label}
                  </button>
                ))}
              </div>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setPreviewOpen(true)}
                size="small"
              >
                Preview wheel
              </Button>
            </div>

            <StepWizard
              instance={assignStepWizard}
              onStepChange={({ activeStep: s }) => setActiveStep(s)}
              isLazyMount
            >
              {/* ── Step 1: Campaign Information ─────────────────────────── */}
              <div className="step-content">
                <h3 className="text-lg font-semibold mb-4">Campaign Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <InputComponent
                      name="Title"
                      type="text"
                      value={title}
                      setValue={setTitle}
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <InputComponent
                      name="Description"
                      type="textarea"
                      value={description}
                      setValue={setDescription}
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <InputComponent
                      name="Start Date"
                      type="date"
                      value={startDate}
                      setValue={setStartDate}
                      required
                    />
                  </div>
                  <div>
                    <InputComponent
                      name="End Date"
                      type="date"
                      value={endDate}
                      setValue={setEndDate}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Budget *
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*\.?[0-9]*"
                      className="w-full border rounded p-2 text-sm"
                      value={budget}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*\.?\d*$/.test(v)) setBudget(v);
                      }}
                      placeholder="e.g. 5000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Scan Per Ticket <span className="text-gray-400">(0 = unlimited)</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="w-full border rounded p-2 text-sm"
                      value={scanPerTicket}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*$/.test(v)) setScanPerTicket(v);
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <SelectComponent
                      title="Status"
                      selected={campaignStatus}
                      setSelected={setCampaignStatus}
                      dataArr={STATUS_OPTIONS}
                    />
                  </div>
                  <div>
                    <SelectComponent
                      title="Feedback Form"
                      selected={feedbackForm}
                      setSelected={setFeedbackForm}
                      dataArr={FEEDBACK_TYPES}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <MultiSelectComponent
                      title="Target States"
                      selected={states}
                      setSelected={setStates}
                      dataArr={allStates}
                    />
                  </div>
                </div>
              </div>

              {/* ── Step 2: Wheel Design ─────────────────────────────────── */}
              <div className="step-content">
                <h3 className="text-lg font-semibold mb-4">Wheel Design</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <SelectComponent
                      title="Segments (2–10)"
                      selected={segments}
                      setSelected={setSegments}
                      dataArr={SEGMENT_OPTIONS}
                      required
                    />
                  </div>
                  <div>
                    <SelectComponent
                      title="Wheel Type"
                      selected={wheelType}
                      setSelected={setWheelType}
                      dataArr={WHEEL_TYPES}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <MultiSelectComponent
                      title="Target User Types"
                      selected={userTypeNames}
                      setSelected={setUserTypeNames}
                      dataArr={userTypes}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Wheel Image *
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        id="wheel-image-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setPictureFile(e.target.files)}
                      />
                      <label
                        htmlFor="wheel-image-upload"
                        className="btn btn-light rounded-pill px-4 py-2 flex items-center gap-2 cursor-pointer border"
                      >
                        <FaCloudUploadAlt />
                        Choose file:{" "}
                        {pictureFile?.length ? pictureFile[0].name?.slice(0, 20) : ""}
                      </label>
                      {pictureFile?.length > 0 && (
                        <Button size="small" variant="contained" onClick={uploadImageFunc}>
                          Upload
                        </Button>
                      )}
                    </div>
                    {picture && (
                      <img
                        src={picture}
                        alt="Wheel"
                        className="mt-2 w-24 h-24 object-contain rounded border"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* ── Step 3: Rewards ──────────────────────────────────────── */}
              <div className="step-content">
                <h3 className="text-lg font-semibold mb-4">Rewards</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Configure each segment. Type and name are required.
                </p>
                <div className="space-y-3">
                  {rewards.map((r, index) => (
                    <div
                      key={index}
                      className="border bg-gray-50 p-3 rounded-lg flex flex-wrap gap-3 items-end"
                    >
                      {/* Segment badge */}
                      <span className="text-xs font-semibold bg-blue-100 text-blue-700 rounded px-2 py-1 self-start">
                        #{index}
                      </span>

                      {/* Type */}
                      <div className="w-28">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Type *
                        </label>
                        <select
                          className="w-full border rounded p-2 text-sm"
                          value={r.type}
                          onChange={(e) => updateReward(index, "type", e.target.value)}
                        >
                          <option value="">Select</option>
                          {REWARD_TYPES.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-[140px]">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Name *
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded p-2 text-sm"
                          value={r.name}
                          onChange={(e) => updateReward(index, "name", e.target.value)}
                          placeholder={index === 0 ? "Better Luck Next Time" : "Reward name"}
                        />
                      </div>

                      {/* Value / Price */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Value / Price
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-full border rounded p-2 text-sm"
                          value={r.price}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || /^\d*\.?\d*$/.test(v))
                              updateReward(index, "price", v);
                          }}
                          placeholder="0"
                        />
                      </div>

                      {/* Points */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Points
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-full border rounded p-2 text-sm"
                          value={r.points}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || /^\d*$/.test(v))
                              updateReward(index, "points", v);
                          }}
                          placeholder="0"
                        />
                      </div>

                      {/* Quantity */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Quantity
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-full border rounded p-2 text-sm"
                          value={r.quantity}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || /^\d*$/.test(v))
                              updateReward(index, "quantity", v);
                          }}
                          placeholder="10"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Step 4: Allocation ───────────────────────────────────── */}
              <div className="step-content">
                <h3 className="text-lg font-semibold mb-4">Allocation Rules</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Set start/end date, chance (%), and optional fields. Total chance should be ≤ 100%.
                </p>
                <div className="space-y-3">
                  {rewards.map((r, index) => (
                    <div
                      key={index}
                      className="border bg-gray-50 p-3 rounded-lg flex flex-wrap gap-3 items-end"
                    >
                      {/* Identity */}
                      <span
                        className="w-36 font-medium text-sm truncate self-center"
                        title={`${r.type} – ${r.name || `#${index}`}`}
                      >
                        {r.type || "—"} – {r.name || `#${index}`}
                      </span>

                      {/* Start Date */}
                      <div className="w-36">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          className="w-full border rounded p-2 text-sm"
                          value={r.startDate || ""}
                          onChange={(e) => updateReward(index, "startDate", e.target.value)}
                        />
                      </div>

                      {/* End Date */}
                      <div className="w-36">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          End Date *
                        </label>
                        <input
                          type="date"
                          className="w-full border rounded p-2 text-sm"
                          value={r.endDate || ""}
                          onChange={(e) => updateReward(index, "endDate", e.target.value)}
                        />
                      </div>

                      {/* Chance % */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Chance % *
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-full border rounded p-2 text-sm"
                          value={r.probability}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || /^\d*\.?\d*$/.test(v))
                              updateReward(index, "probability", v);
                          }}
                          placeholder="0"
                        />
                      </div>

                      {/* Quantity (also editable in allocation) */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Quantity
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-full border rounded p-2 text-sm"
                          value={r.quantity}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || /^\d*$/.test(v))
                              updateReward(index, "quantity", v);
                          }}
                          placeholder="10"
                        />
                      </div>

                      {/* Status */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Status
                        </label>
                        <select
                          className="w-full border rounded p-2 text-sm"
                          value={r.status}
                          onChange={(e) => updateReward(index, "status", e.target.value)}
                        >
                          <option value="2">Active</option>
                          <option value="1">Inactive</option>
                        </select>
                      </div>

                      {/* Coupon type */}
                      <div className="w-28">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Coupon type
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded p-2 text-sm"
                          value={r.coupon_type || ""}
                          onChange={(e) => updateReward(index, "coupon_type", e.target.value)}
                          placeholder="Optional"
                        />
                      </div>

                      {/* Font size */}
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Font size
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded p-2 text-sm"
                          value={r.font_size || ""}
                          onChange={(e) => updateReward(index, "font_size", e.target.value)}
                          placeholder="e.g. 14"
                        />
                      </div>

                      {/* Is Reward checkbox */}
                      <div className="flex items-center gap-1 self-end pb-2">
                        <input
                          type="checkbox"
                          id={`is_reward_${index}`}
                          checked={r.is_reward === "1"}
                          onChange={(e) =>
                            updateReward(index, "is_reward", e.target.checked ? "1" : "0")
                          }
                          className="rounded"
                        />
                        <label
                          htmlFor={`is_reward_${index}`}
                          className="text-xs font-medium text-gray-700"
                        >
                          Is reward
                        </label>
                      </div>

                      {/* Locations */}
                      <div className="w-48 min-w-0">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          Locations (States)
                        </label>
                        <MultiSelectComponent
                          title=""
                          selected={r.locations || []}
                          setSelected={(val) => updateReward(index, "locations", val)}
                          dataArr={allStates}
                        />
                        <p className="text-xs text-gray-400 mt-0.5">
                          Empty = all states.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </StepWizard>

            {/* Navigation buttons */}
            <ActionButtons
              currentStep={activeStep}
              totalSteps={4}
              previousStep={() => stepWizardRef.current?.previousStep?.()}
              nextStep={() => stepWizardRef.current?.nextStep?.()}
              onStep1Next={handleStep1Next}
              onStep2Next={handleStep2Next}
              onStep4Submit={handleStep4Submit}
              isSubmitting={isSubmitting}
            />

            {/* Preview modal */}
            <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 3,
                  borderRadius: 2,
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  overflow: "auto",
                }}
              >
                <h3 className="text-lg font-semibold mb-3">Wheel preview</h3>
                <div className="flex justify-center p-4 bg-gray-100 rounded-lg">
                  <Wheels
                    data={
                      rewards.length > 0
                        ? rewards.map((r, i) => ({
                            option: r.name?.trim() || `Segment ${i + 1}`,
                          }))
                        : [{ option: "Segment 1" }, { option: "Segment 2" }]
                    }
                    backgroundColor={["#3e3e3e", "#df3428"]}
                    textColors={["#ffffff", "#ffffff"]}
                    outerBorderColor="#000000"
                    fontSize="14"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Segments: {rewards.length || 2}. Fill Rewards step to see names.
                </p>
                <Button
                  variant="contained"
                  onClick={() => setPreviewOpen(false)}
                  className="mt-3"
                >
                  Close
                </Button>
              </Box>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWheel;
